import { FC, useEffect } from "react";

import { config } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css"; // Only if you manually include CSS

import "@/styles/styles.scss";
import ToastAlert from "@components/core/alerts/ToastAlert";
import CookiesBanner from "@components/core/banners/CookiesBanner";
import E2EHook from "@components/core/e2e/E2EHook";
import AppInfosRibbon from "@components/core/ribbons/AppInfosRibbon";
import coreRoutes from "@routes/core/CoreRoutes";
import { authenticationStore } from "@state/auth/AuthStore";
import { requestGetDegradedMode } from "@state/degraded/DegradedEffects";
import { requestGetLogoutDetails } from "@state/logoutDetails/LogoutDetailsEffects";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import configuration from "@utils/Config";
import "@utils/i18n/i18n";
import { ConfigProvider } from "antd";
import { useUnit } from "effector-react";
import { Routes, useNavigate } from "react-router-dom";
import { requestGetTransactionSettings } from "@state/transactions/TransactionEffects";
import { UserPictureProvider } from "@utils/contexts/UserPictureContext";
import { ROUTES } from "@routes/Routes";
import { updateSelectedOfficeIds } from "@state/auth/AuthEvents";
import { OfficeHelper } from "@utils/helpers/office-helper";
import { NonceHelper } from "@utils/helpers/nonce-helper";

// CSP security rule
// Disable automatic CSS injection
config.autoAddCss = false;

export const App: FC = () => {
  const navigate = useNavigate();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  useEffect(() => {
    if (authStore?.user) {
      // Initializing store with needed endpoints
      void requestGetDegradedMode({});
      void requestGetLogoutDetails({});
      void requestGetTransactionSettings({});

      if (!authStore.user.onboarding && authStore.user.cgu) {
        navigate(ROUTES.extranet.cgu.generate());
      }

      updateSelectedOfficeIds(
        OfficeHelper.mapOfficeToSelectedOfficesPayload(
          authStore?.user?.offices ?? [],
        ),
      );
    }
  }, [authStore?.user]);

  return (
    <ConfigProvider
      csp={{ nonce: NonceHelper.getNonceValue() }}
      theme={{
        token: {
          colorPrimary: "#008E96",
          colorLink: "#4d4d4d",
          colorSuccess: "#28a745",
          colorWarning: "#ffc107",
          colorError: "#F42640",
          fontSize: 16,
          lineHeight: 1.5,
          colorTextHeading: "#000000",
          colorText: "#6c757d",
          colorTextSecondary: "#008E96",
          colorTextDisabled: "#6c757d",
          borderRadius: 0,
          colorBorder: "#dee2e6",
          boxShadow: "0 0.05rem 0.25rem rgba(0, 0, 0, 0.1)",
          fontFamily: "sans-serif",
        },
      }}
    >
      <UserPictureProvider>
        <div className="global">
          <E2EHook />
          <CookiesBanner />
          {configuration.appInfosActive && <AppInfosRibbon />}
          <ToastAlert />
          <Routes>{coreRoutes}</Routes>
        </div>
      </UserPictureProvider>
    </ConfigProvider>
  );
};

export default App;
